import {
  AppIcons,
  ColorToken,
  EarlyCareerHires,
  CareerCategory,
  CareerPath,
  TIERS,
  CareerPathCompensation,
  CareerTrack,
  CareerPathSampleJobs,
  AI_IMPACT_AUTOMATION_RISK,
  AI_IMPACT_INTEGRATION_INTENSITY,
  AI_IMPACT_ROLE_REINVENTION_NEED,
  AI_IMPACT_LEVERAGE_UPSIDE,
  AI_IMPACT_HUMAN_DIFFERENTIATOR_INDEX,
  CareerPathAiImpact,
  DoubleSelectDropdownOption,
  CareerPathGrowth,
  CaregiverFlexibilityRating,
  CareerPathFitLevel,
  TIER_KEY_TO_COLOR,
  CareerPathAIDisruption,
  CareerCategorySlug,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CareerPathWithScore } from '@backrs_ui/grow-ui/src/models/models';
import { filterCareerPathSuggestionsByScoreSection } from '@backrs_ui/grow-ui/src/utils/careerPath/careerPathSuggestions';

import { CareerTrackDistributionItem } from './careerPath.types';

function cleanPercentage(value: string): number | null {
  const cleaned = value.replace(/[<>~%]/g, '').trim();
  const num = parseFloat(cleaned);

  return isNaN(num) ? null : num;
}

export function transformCareerTrackDistributionData(
  apiData: Record<string, string>,
): CareerTrackDistributionItem[] {
  const TIER_LABELS = appStrings.modules.careerPath.detailPage.trackDistribution.tierLabels;

  return Object.entries(apiData).map(([apiKey, rawValue]) => {
    const tierKey = apiKey as keyof typeof TIERS;
    const value = cleanPercentage(rawValue);

    const getCustomLabel = (tier: keyof typeof TIERS): string => {
      switch (tier) {
        case 'TRADE':
          return TIER_LABELS.trade;
        case 'MID':
          return TIER_LABELS.mid;
        case 'UPPER':
          return TIER_LABELS.upper;
        case 'TOP':
          return TIER_LABELS.top;
        case 'SEMISKILLED':
          return TIER_LABELS.semiskilled;
        default:
          return TIERS[tier];
      }
    };

    return {
      key: apiKey,
      tierKey,
      label: getCustomLabel(tierKey),
      color: TIER_KEY_TO_COLOR[tierKey],
      value,
    };
  });
}

/**
 * Generic type for icon and label mapping
 */
type IconAndLabel = Readonly<{
  icon: AppIcons;
  label: string;
}>;

/**
 * Generic function to map values to icons and labels using a mapping object
 */
function mapValueToIconAndLabel<T extends string>(
  value: T | undefined,
  mapping: Record<T, IconAndLabel>,
  fallback: IconAndLabel,
): IconAndLabel {
  if (!value || !(value in mapping)) {
    return fallback;
  }

  return mapping[value];
}

// Define the mappings as constants
export const GROWTH_MAPPING: Record<CareerPathGrowth, IconAndLabel> = {
  Negative: { icon: 'TrendDown02', label: 'Negative' },
  Stable: { icon: 'ArrowRight', label: 'Stable' },
  Low: { icon: 'TrendDown01', label: 'Low' },
  Medium: { icon: 'TrendUp01', label: 'Medium' },
  High: { icon: 'TrendUp02', label: 'High' },
} as const;

const CAREGIVER_FLEXIBILITY_MAPPING: Record<CaregiverFlexibilityRating, IconAndLabel> = {
  High: { icon: 'TrendUp02', label: 'High' },
  Medium: { icon: 'TrendUp01', label: 'Medium' },
  Low: { icon: 'TrendDown01', label: 'Low' },
} as const;

const EARLY_CAREER_HIRES_MAPPING: Record<string, IconAndLabel> = {
  Low: { icon: 'TrendDown01', label: 'Low' },
  Medium: { icon: 'TrendUp01', label: 'Medium' },
  High: { icon: 'TrendUp02', label: 'High' },
} as const;

// Updated functions using the generic mapper
export const getGrowthIconAndLabel = (growth: CareerPathGrowth): IconAndLabel => {
  return mapValueToIconAndLabel(growth, GROWTH_MAPPING, { icon: 'TrendUp01', label: growth });
};

export const getCaregiverFlexibilityIconAndLabel = (
  flexibility: CaregiverFlexibilityRating | undefined,
): IconAndLabel => {
  return mapValueToIconAndLabel(flexibility, CAREGIVER_FLEXIBILITY_MAPPING, {
    icon: 'TrendUp01',
    label: flexibility || 'Unknown',
  });
};

// Update the existing early career hires function to use the same pattern
function categorizeHires(count: number): IconAndLabel {
  if (count < 25000) {
    return EARLY_CAREER_HIRES_MAPPING.Low;
  } else if (count < 100000) {
    return EARLY_CAREER_HIRES_MAPPING.Medium;
  } else {
    return EARLY_CAREER_HIRES_MAPPING.High;
  }
}

export function transformEarlyCareerHiresTotal(hires: EarlyCareerHires): IconAndLabel {
  const total = hires.skilled_technical + hires.professional;

  return categorizeHires(total);
}

// TODO: Use the same function from results.util.tsx, will require multiple updates to use new colors instead of app colors
export const getCareerPathFitColor = (
  scoreSection: CareerPathWithScore['scoreSection'] | undefined,
): ColorToken => {
  if (scoreSection === 'TOP') {
    return 'Brand/700';
  }

  if (scoreSection === 'MIDDLE') {
    return 'Blue/500';
  }

  return 'Blue/100';
};

const ICON_MAP: Record<CareerCategorySlug, AppIcons> = {
  agriculture: 'SunSetting02',
  arts: 'Palette',
  business: 'Briefcase01',
  education: 'Telescope',
  law: 'Scales01',
  'patient-care': 'MedicalSquare',
  stem: 'Atom01',
  'social-sector': 'Hand',
  trades: 'Tool02',
} as const;

export const getCareerPathCategoryName = (
  careerCategories: CareerCategory[],
  careerPath: CareerPath,
) => {
  const category = careerCategories.find(
    (category) => category.id === careerPath.career_category_id,
  );

  return category?.name || '';
};

export const getCareerPathCategorySlug = (
  careerCategories: CareerCategory[],
  careerPath: CareerPath,
): CareerCategorySlug => {
  const category = careerCategories.find(
    (category) => category.id === careerPath.career_category_id,
  );

  if (!category) return 'business';

  return category.slug;
};

export const getCareerPathIcon = (
  careerCategories: CareerCategory[],
  careerPath: CareerPath,
): AppIcons => {
  const categorySlug = getCareerPathCategorySlug(careerCategories, careerPath);

  if (!categorySlug) return ICON_MAP['business'];

  return ICON_MAP[categorySlug];
};

export const getCareerCategoryShortName = (
  careerCategories: CareerCategory[],
  careerPath: CareerPath,
): string => {
  const category = careerCategories.find(
    (category) => category.id === careerPath.career_category_id,
  );

  if (!category) return 'Business';

  return category.short_name;
};

// wanna create a function that returns all data needed for blueprint strong fit path card
export const getCareerPathCardData = ({
  careerPath,
  careerPathSuggestions,
  sortedCareerCategories,
}: {
  careerPath: CareerPath;
  careerPathSuggestions?: CareerPathWithScore[];
  sortedCareerCategories: CareerCategory[];
}) => {
  const shortName = getCareerCategoryShortName(sortedCareerCategories, careerPath);
  const categoryIcon = getCareerPathIcon(sortedCareerCategories, careerPath);
  const fitLevel = getCareerPathFitLevelLabel({
    careerPathId: careerPath.id,
    careerPathSuggestions: careerPathSuggestions || [],
  });
  const fitColor = getCareerPathFitColor(
    careerPathSuggestions?.find((suggestion) => suggestion.careerPath.id === careerPath.id)
      ?.scoreSection || 'LOW',
  );

  return {
    shortName,
    categoryIcon,
    fitLevel,
    fitColor,
  };
};

/**
 * Gets compensation data for a specific career path and career track
 * @param careerPathId - The ID of the career path
 * @param careerTrackId - The ID of the career track
 * @param careerPathCompensations - Array of all career path compensations from Redux state
 * @returns The compensation data for the specific career path and track, or null if not found
 */
export const getCareerPathCompensation = (
  careerPathId: number,
  careerTrackId: number,
  careerPathCompensations: CareerPathCompensation[],
): CareerPathCompensation | null => {
  return (
    careerPathCompensations.find(
      (compensation) =>
        compensation.career_path_id === careerPathId &&
        compensation.career_track_id === careerTrackId,
    ) || null
  );
};

/**
 * Gets all compensation data for a specific career path across all tracks
 * @param careerPathId - The ID of the career path
 * @param careerPathCompensations - Array of all career path compensations from Redux state
 * @returns Array of compensation data for the specific career path
 */
export const getCareerPathCompensationsByPathId = (
  careerPathId: number,
  careerPathCompensations: CareerPathCompensation[],
): CareerPathCompensation[] => {
  return careerPathCompensations.filter(
    (compensation) => compensation.career_path_id === careerPathId,
  );
};

/**
 * Gets all sample job data for a specific career path
 * @param careerPathId - The ID of the career path
 * @param careerPathSampleJobs - Array of sample jobs from Redux state
 * @param careerTracks - Array of all career tracks from Redux state
 * @returns Array of sample job data for the specific career path
 */

export const getCareerPathSampleJobsByPathId = (
  careerPathId: number,
  careerPathSampleJobs: CareerPathSampleJobs[],
  careerTracks: CareerTrack[],
): CareerPathSampleJobs => {
  const found = careerPathSampleJobs.find((sampleJob) => sampleJob.career_path_id === careerPathId);

  if (found) {
    return found;
  }

  // Create empty tiers object using career track names from Redux state
  const emptyTiers = careerTracks.reduce(
    (acc, track) => {
      acc[track.name] = [];

      return acc;
    },
    {} as CareerPathSampleJobs['tiers'],
  );

  // Return empty structure if no sample jobs found
  return {
    career_path_id: careerPathId,
    tiers: emptyTiers,
  };
};

export const getCareerPathAIDisruptionByPathId = (
  careerPathAIDisruptions: CareerPathAIDisruption[],
  careerPathId: number,
): CareerPathAIDisruption => {
  return (
    careerPathAIDisruptions.find((disruption) => disruption.career_path_id === careerPathId) ||
    ({} as CareerPathAIDisruption)
  );
};

/**
 * Gets the career track name by ID
 * @param careerTrackId - The ID of the career track
 * @param careerTracks - Array of all career tracks from Redux state
 * @returns The career track name if found, or a fallback string
 */
export const getCareerTrackName = (careerTrackId: number, careerTracks: CareerTrack[]): string => {
  const track = careerTracks.find((track) => track.id === careerTrackId);

  return track?.name || `Track ${careerTrackId}`;
};

/**
 * Groups career path compensations by track name
 * @param careerPathCompensations - Array of career path compensations
 * @param careerTracks - Array of all career tracks from Redux state
 * @returns Object with track names as keys and arrays of compensations as values
 */
export const groupCompensationsByTrack = (
  careerPathCompensations: CareerPathCompensation[],
  careerTracks: CareerTrack[],
): Record<string, CareerPathCompensation[]> => {
  return careerPathCompensations.reduce(
    (acc, compensation) => {
      const trackName = getCareerTrackName(compensation.career_track_id, careerTracks);

      if (!acc[trackName]) {
        acc[trackName] = [];
      }

      acc[trackName].push(compensation);

      return acc;
    },
    {} as Record<string, CareerPathCompensation[]>,
  );
};

/**
 * AI Impact Utilities
 *
 * in career paths. They provide consistent icon mapping, color schemes, and
 * modal data generation for the five AI impact categories.
 */

const AI_IMPACT_STRINGS = appStrings.modules.careerPath.detailPage.aiImpact;

/**
 * Maps AI impact field names to their display configuration.
 * Each entry contains the title, level mapping, description, impact key, and icon
 * needed for rendering and modal interactions.
 */
export const AI_IMPACT_MAPPING = {
  ai_impact_automation_risk: {
    title: AI_IMPACT_STRINGS.modalSections.automationRisk,
    levelMapping: AI_IMPACT_AUTOMATION_RISK,
    description: AI_IMPACT_STRINGS.descriptions.automationRisk,
    impactKey: 'automationRisk',
    iconName: 'AlertTriangle',
    disruptionKey: 'automation_risk',
  },
  ai_impact_leverage_upside: {
    title: AI_IMPACT_STRINGS.modalSections.aiLeverageUpside,
    levelMapping: AI_IMPACT_LEVERAGE_UPSIDE,
    description: AI_IMPACT_STRINGS.descriptions.leverageUpside,
    impactKey: 'leverageUpside',
    iconName: 'UserEdit',
    disruptionKey: 'ai_leverage_upside',
  },
  ai_impact_integration_intensity: {
    title: AI_IMPACT_STRINGS.modalSections.aiIntegrationIntensity,
    levelMapping: AI_IMPACT_INTEGRATION_INTENSITY,
    description: AI_IMPACT_STRINGS.descriptions.integrationIntensity,
    impactKey: 'integrationIntensity',
    iconName: 'CpuChip01',
    disruptionKey: 'ai_integration_intensity',
  },
  ai_impact_human_differentiator_index: {
    title: AI_IMPACT_STRINGS.modalSections.humanDifferentiatorIndex,
    levelMapping: AI_IMPACT_HUMAN_DIFFERENTIATOR_INDEX,
    description: AI_IMPACT_STRINGS.descriptions.humanDifferentiatorIndex,
    impactKey: 'humanDifferentiatorIndex',
    iconName: 'PuzzlePiece01',
    disruptionKey: 'human_differentiator_index',
  },
  ai_impact_role_reinvention_need: {
    title: AI_IMPACT_STRINGS.modalSections.roleReinventionNeeded,
    levelMapping: AI_IMPACT_ROLE_REINVENTION_NEED,
    description: AI_IMPACT_STRINGS.descriptions.roleReinventionNeed,
    impactKey: 'roleReinventionNeed',
    iconName: 'TrendUp01',
    disruptionKey: 'role_reinvention_need',
  },
} as const;

type AiImpactField = keyof typeof AI_IMPACT_MAPPING;

/**
 * Creates structured data for AI impact sections with field name, value, and level
 * @param aiImpact - The AI impact data containing all 5 impact metrics
 * @returns Array of objects with field name, value, level, and display info
 */
type CreateAIImpactSectionDataResponse = Readonly<{
  field: string;
  title: string;
  description: string;
  value: string;
  level: number;
  color: ColorToken;
  iconName: AppIcons;
  config: (typeof AI_IMPACT_MAPPING)[AiImpactField];
}>;
export const createAiImpactSectionData = (
  aiImpact: CareerPathAiImpact,
  disruption?: CareerPathAIDisruption,
) => {
  return (
    Object.entries(AI_IMPACT_MAPPING) as [
      AiImpactField,
      (typeof AI_IMPACT_MAPPING)[AiImpactField],
    ][]
  )
    .map(([field, config]) => {
      const value = aiImpact[field];

      if (!value) return null;

      const level = getLevelFromValue(value);

      const disruptionKey = config.disruptionKey as keyof CareerPathAIDisruption;

      return {
        field,
        title: config.title,
        description: String(disruption?.[disruptionKey] ?? config.description),
        value,
        level,
        iconName: config.iconName,
        config,
        color: getAiImpactLevelColor(level),
      } satisfies CreateAIImpactSectionDataResponse;
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);
};

// Helper function to get color based on AI impact level
const getAiImpactLevelColor = (level: number): ColorToken => {
  switch (level) {
    case 1:
      return 'Brand/300';
    case 2:
      return 'Brand/500';
    case 3:
      return 'Brand/700';
    case 4:
      return 'Brand/900';
    default:
      return 'Brand/900';
  }
};

/**
 * Creates modal data structure for ChartCardModal from AI impact data
 * @param aiImpact - The AI impact data containing all 5 impact metrics
 * @returns ModalData structure expected by ChartCardModal
 */
export const createAiImpactModalData = (aiImpact: CareerPathAiImpact) => {
  const sections = createAiImpactSectionData(aiImpact);

  return {
    title: AI_IMPACT_STRINGS.impactOfAi,
    description: AI_IMPACT_STRINGS.aiImpactDescription,
    modalCards: sections.map((item) => ({
      iconName: item.iconName,
      color: 'Brand/500' as const, // fixed color for modal
      title: item.title,
      content: String(item.description),
      level: item.level.toString(),
      subtitle: '',
    })),
  };
};

/**
 * Helper function to get the numeric level (1-4) for any AI impact value
 */
const getLevelFromValue = (value: string | undefined): number => {
  if (!value) return 1;

  // Check all level mappings to find which level this value belongs to
  const allMappings = [
    AI_IMPACT_AUTOMATION_RISK,
    AI_IMPACT_INTEGRATION_INTENSITY,
    AI_IMPACT_ROLE_REINVENTION_NEED,
    AI_IMPACT_LEVERAGE_UPSIDE,
    AI_IMPACT_HUMAN_DIFFERENTIATOR_INDEX,
  ] as const;

  for (let level = 1; level <= 4; level++) {
    const levelKey = `Level_${level}` as const;

    if (
      allMappings.some((mapping) => {
        return mapping[levelKey as keyof typeof mapping] === value;
      })
    ) {
      return level;
    }
  }

  // Fallback to level 1 if not found
  return 1;
};

/**
 * Calculates the overall AI impact level based on the 5 individual AI impact metrics.
 *
 * New methodology: Sum all 5 attributes (each on scale 1-4) for total range of 5-20
 * 5-7 = Low
 * 8-12 = Moderate
 * 13-17 = High
 * 18-20 = Very High
 *
 * @param aiImpact - The AI impact data containing all 5 impact metrics
 * @returns Object with the total score (5-20) and corresponding score text
 */
export const calculateOverallAiImpact = (
  aiImpact: CareerPathAiImpact,
): { level: number; score: string } => {
  // Get all available impact values
  const impactValues = [
    aiImpact.ai_impact_automation_risk,
    aiImpact.ai_impact_integration_intensity,
    aiImpact.ai_impact_role_reinvention_need,
    aiImpact.ai_impact_leverage_upside,
    aiImpact.ai_impact_human_differentiator_index,
  ].filter(Boolean);

  if (impactValues.length === 0) {
    return { level: 5, score: 'Low' };
  }

  // Convert values to levels (1-4) and sum them
  const levels = impactValues.map(getLevelFromValue);
  const totalScore = levels.reduce((sum, level) => sum + level, 0);

  // Map total score to categories
  const score = (() => {
    if (totalScore >= 5 && totalScore <= 7) return 'Low';
    if (totalScore >= 8 && totalScore <= 12) return 'Moderate';
    if (totalScore >= 13 && totalScore <= 17) return 'High';
    if (totalScore >= 18 && totalScore <= 20) return 'Very High';

    return 'Low'; // Fallback for edge cases
  })();

  return {
    level: totalScore,
    score,
  };
};

/**
 * Transforms career categories into dropdown options
 * @param categories - Array of career categories from Redux state
 * @param careerPathSuggestions - Optional array of career path suggestions to determine strong fits
 * @returns Array of dropdown options with value (category ID), label (category name), and showDot flag
 */
export function transformCareerCategories(
  categories: CareerCategory[],
  careerPathSuggestions?: CareerPathWithScore[],
): DoubleSelectDropdownOption[] {
  return categories.map((category) => {
    // Determine if this category has any strong fits
    const hasStrongFits = careerPathSuggestions
      ? getStrongFitCountForCategory(category.id.toString(), careerPathSuggestions, [category])
      : false;

    return {
      value: category.id.toString(),
      label: category.name,
      dotColor: (!!hasStrongFits && 'Brand/700') || undefined, // Show dot if there are strong fits
    };
  });
}

/**
 * Transforms career paths into dropdown options
 * @param paths - Array of career paths filtered by selected category
 * @returns Array of dropdown options with value (path name) and label (path name)
 */
export function transformCareerPaths(paths: CareerPathWithScore[]): DoubleSelectDropdownOption[] {
  return paths.map((path) => ({
    value: path.careerPath.name,
    label: path.careerPath.name,
    dotColor: getCareerPathFitColor(path.scoreSection),
  }));
}

/**
 * Calculates the number of strong fit career paths within a specific category
 *
 * This function:
 * 1. Filters all career path suggestions to only include 'TOP' (strong fit) ones
 * 2. Further filters those to only include paths in the specified category
 * 3. Returns the count of strong fit paths in that category
 *
 * @param categoryId - The ID of the category to count strong fits for
 * @param careerPathSuggestions - Array of career path suggestions (can be null if no survey results)
 * @param careerCategories - Array of all career categories
 * @returns The number of strong fit career paths in the specified category
 */
export const getStrongFitCountForCategory = (
  categoryId: string,
  careerPathSuggestions: CareerPathWithScore[] | undefined,
  careerCategories: CareerCategory[],
): number => {
  // Early return if no survey results are available
  if (!careerPathSuggestions) return 0;

  // Find the category object for the given ID
  const selectedCategory = careerCategories.find((cat) => cat.id.toString() === categoryId);

  if (!selectedCategory) return 0;

  // Get all career paths that are marked as 'TOP' (strong fit)
  const strongFitPaths = filterCareerPathSuggestionsByScoreSection(careerPathSuggestions, 'TOP');

  // Filter strong fit paths to only include those in the selected category
  const strongFitPathsInCategory = strongFitPaths.filter(
    (suggestion) => suggestion.careerPath.career_category_id === selectedCategory.id,
  );

  return strongFitPathsInCategory.length;
};

export const getCareerPathsForCategory = (
  categoryId: number,
  careerPaths: CareerPath[],
): CareerPath[] => {
  return careerPaths
    .filter((path) => path.career_category_id === categoryId)
    .sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Gets the icon for a category by slug
 * @param categorySlug - The slug of the category
 * @returns The icon name for the category
 */
export const getCategoryIcon = (categorySlug: CareerCategorySlug): AppIcons => {
  return ICON_MAP[categorySlug] || ICON_MAP['business'];
};

/**
 * CareerPathPage Component Utilities
 *
 * These utilities are specifically used by the CareerPathPage components
 * (StrongFitPaths, AllCareerPaths, CareerPathHeader) for consistent
 * category name display and data transformation.
 */

/**
 * Maps category names to their descriptions from appStrings
 * Used in WhatIsACareerPathModal to show category descriptions in tooltips
 *
 * @param categoryName - The full category name from the database
 * @returns The description string from appStrings.ts
 */
export const getCategoryDescription = (categorySlug: CareerCategorySlug): string => {
  const STRINGS = appStrings.modules.careerPath.summaryPage.whatIsACareerPathModal.topRightSection;

  return STRINGS[categorySlug];
};

/**
  Sorts career categories based on the number of high/medium fit career paths
  and the total number of career paths in each category
  * @param careerCategories - Array of career categories to sort
  * @param careerPathSuggestions - Array of career path suggestions to determine strong fits
  * @returns Sorted array of career categories
*/
export const sortCareerCategories = (
  careerCategories: CareerCategory[],
  careerPathSuggestions: CareerPathWithScore[] | undefined,
) => {
  return [...careerCategories].sort((a, b) => {
    const aPaths = careerPathSuggestions
      ? careerPathSuggestions.filter(
          (suggestion) => suggestion.careerPath.career_category_id === a.id,
        )
      : [];

    const bPaths = careerPathSuggestions
      ? careerPathSuggestions.filter(
          (suggestion) => suggestion.careerPath.career_category_id === b.id,
        )
      : [];

    const aHighMediumCount =
      filterCareerPathSuggestionsByScoreSection(aPaths, 'TOP').length +
      filterCareerPathSuggestionsByScoreSection(aPaths, 'MIDDLE').length;
    const bHighMediumCount =
      filterCareerPathSuggestionsByScoreSection(bPaths, 'TOP').length +
      filterCareerPathSuggestionsByScoreSection(bPaths, 'MIDDLE').length;

    if (aHighMediumCount !== bHighMediumCount) {
      return bHighMediumCount - aHighMediumCount; // Descending order
    }

    return bPaths.length - aPaths.length; // Descending order by total paths
  });
};

export const FIT_SCORE_LEVEL_MAP: Record<string, CareerPathFitLevel> = {
  TOP: 'Strong Fit',
  MIDDLE: 'Medium Fit',
  LOW: 'Low Fit',
};

/**
 * Gets the fit level for a career path based on the career path suggestions
 * @param careerPathId - The ID of the career path
 * @param careerPathSuggestions - Array of career path suggestions to determine fit level
 * @returns The fit level for the career path
 */

export const getCareerPathFitLevelLabel = ({
  careerPathId,
  careerPathSuggestions,
}: {
  careerPathId?: number;
  careerPathSuggestions?: CareerPathWithScore[];
}) => {
  const suggestion = careerPathSuggestions?.find(
    (suggestion) => suggestion.careerPath.id === careerPathId,
  );

  return FIT_SCORE_LEVEL_MAP[suggestion?.scoreSection || 'LOW'] || 'Low Fit';
};

/**
 * Filters career paths based on the selected category and fit level - only returns strong or medium fit paths,
 * ordered with TOP fits first, followed by MIDDLE fits.
 * @param selectedCategoryId - The ID of the selected category
 * @param careerPathSuggestions - Array of career path suggestions to determine fit level
 * @returns Filtered and ordered array of career paths
 */
export const filterCareerPathsByCategory = (
  selectedCategoryId: string | undefined,
  careerPathSuggestions: CareerPathWithScore[] | undefined,
) => {
  if (!careerPathSuggestions) return [];

  return careerPathSuggestions
    .filter(({ careerPath, scoreSection }) => {
      const isInSelectedCategory =
        !selectedCategoryId || careerPath.career_category_id.toString() === selectedCategoryId;

      const isStrongOrMediumFit = scoreSection === 'TOP' || scoreSection === 'MIDDLE';

      return isInSelectedCategory && isStrongOrMediumFit;
    })
    .map(({ careerPath }) => careerPath);
};
